// src/Pages/ReleaseDetails.js
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { api, API_BASE } from '../api';
import { useAudio } from '../contexts/AudioContext';
import ReleaseInfocard from '../Components/ReleaseInfocard';

/* ─── assets ─── */
import trashIcon from '../assets/icons/trash.svg';
import archiveIcon from '../assets/icons/archive.svg';
import emptyArt from '../assets/emptyart.jpg';
import backArrowIcon from '../assets/icons/backarrow.svg';
import infoIcon from '../assets/icons/info.svg';
import mailIcon from '../assets/icons/mail.svg';
import { ReactComponent as DownloadIcon } from '../assets/icons/download.svg';
import { ReactComponent as CheckCircleIcon } from '../assets/icons/checkcircle.svg';


import AIcon from '../assets/icons/A.svg';
import CIcon from '../assets/icons/C.svg';
import DCIcon from '../assets/icons/DC.svg';
import DocIcon from '../assets/icons/DocIcon.svg';
import PIcon from '../assets/icons/P.svg';
import PencilTemplateIcon from '../assets/icons/PenciTemplate.svg';

/* components */
import ReleaseModal from '../Components/ReleaseModal';
import SelectTodoTemplate from '../Components/SelectTodoTemplate';
import SpotifyScheduling from '../Components/SpotifyScheduling';
import ReleaseEmails from './ReleaseEmails';

/* map template icon filename → imported svg */
const iconMap = {
  'A.svg': AIcon,
  'C.svg': CIcon,
  'DC.svg': DCIcon,
  'DocIcon.svg': DocIcon,
  'P.svg': PIcon,
  'PenciTemplate.svg': PencilTemplateIcon,
};

export default function ReleaseDetails({ templates = [] }) {
  /* ───────── state ───────── */
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { playTrack, currentTrack, togglePlayPause, isPlaying: globalIsPlaying } = useAudio();
const fromProject  = location.state?.from === 'project'  && location.state?.projectId;
const fromCalendar = location.state?.from === 'calendar';
const calendarDateKey = location.state?.dateKey || null; // e.g. "2025-09-12"
const [infoOpen, setInfoOpen] = useState(false);

// If you have a calendar route that can accept a focused date, you could build it here.
// For now we’ll just go to the calendar root.
const calendarPath = '/calendar';

const backTo = fromProject
  ? `/project/${location.state.projectId}`
  : fromCalendar
  ? calendarPath
  : '/track-overview';

  const [release, setRelease] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* overdue banner */
  const [overdueCount, setOverdueCount] = useState(0);

  /* edit modal state */
  const [isModalOpen, setIsModalOpen] = useState(false);

  /* confirm modals */
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  
  /* email modal */
  const [showEmailModal, setShowEmailModal] = useState(false);

  /* audio player */
  const [mp3SignedUrl, setMp3SignedUrl] = useState(null);

    /* identifiers UI */
  const [identsOpen, setIdentsOpen] = useState(false);
  const [identsEditing, setIdentsEditing] = useState(false);
  const [assigning, setAssigning] = useState({}); // { [`${releaseId}_isrc` | `_trackCatalog` | `_trackUpc`]: boolean }
  const [identsDraft, setIdentsDraft] = useState({
    isrc: '',
    trackCatalogNumber: '',
    trackUpc: '',
  });

  const isSingle = (release?.releaseType || '') === 'Single';

  /* links UI */
const [linksOpen, setLinksOpen] = useState(false);
// in-flight guards
const [linksSaving, setLinksSaving] = useState(false);
const [identsSaving, setIdentsSaving] = useState(false);
const [linksEditing, setLinksEditing] = useState(false);
const [linksDraft, setLinksDraft] = useState({
  presaveLink: '',
  soundcloudUrl: '',
  spotifyUrl: '',
  discoUrl: '',
});

  // whenever we open identifiers (or release changes), seed the draft
  useEffect(() => {
    if (!release) return;
    setIdentsDraft({
      isrc: release.isrc || '',
      trackCatalogNumber: release.trackCatalogNumber || '',
      trackUpc: release.trackUpc || '',
    });
  }, [release]);

  // whenever we open links (or release changes), seed the links draft
useEffect(() => {
  if (!release) return;
  setLinksDraft({
    presaveLink: release.presaveLink || '',
    soundcloudUrl: release.soundcloudUrl || '',
    spotifyUrl: release.spotifyUrl || '',
    discoUrl: release.discoUrl || '',
  });
  }, [release]);

  /* search helpers for ReleaseModal props */
  const [allArtists, setAllArtists] = useState([]);
  const [allProjects, setAllProjects] = useState([]);

  /* template tab */
  const [activeTab, setActiveTab] = useState('tasks');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [pendingTemplateId, setPendingTemplateId] = useState('');
  const [showTemplateConfirm, setShowTemplateConfirm] = useState(false);
  
  /* export buttons state */
  const [districtAppending, setDistrictAppending] = useState(false);
  const [killphonicAppending, setKillphonicAppending] = useState(false);
  const [creatorsAppending, setCreatorsAppending] = useState(false);

  /* ───────── fetch release ───────── */
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await api(`/api/releases/${id}`);
        if (!res.ok) throw new Error(`Fetch error (${res.status})`);
        const data = await res.json();
        setRelease(data);
        if (data.templateId) {
          const tid =
            typeof data.templateId === 'object'
              ? data.templateId._id
              : data.templateId;
          setSelectedTemplateId(String(tid));
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  /* ───────── fetch artists & projects (for modal dropdowns) ───────── */
  useEffect(() => {
    const load = async () => {
      try {
        const [aRes, pRes] = await Promise.all([
          api('/api/artists'),
          api('/api/projects'),
        ]);
        setAllArtists(await aRes.json());
        setAllProjects(await pRes.json());
      } catch (err) {
        console.error(err);
      }
    };
    load();
  }, []);

  /* ───────── overdue count ───────── */
  useEffect(() => {
    if (!release || !release.releaseTasks || !release.releaseDate) return;
    const isOverdue = (task) => {
      if (task.status === 'Completed' || !task.daysBefore) return false;
      const due = new Date(release.releaseDate);
      due.setDate(due.getDate() - Number(task.daysBefore));
      return due < new Date();
    };
    setOverdueCount(release.releaseTasks.filter(isOverdue).length);
  }, [release]);

  /* ───────── fetch signed URL for MP3 Master ───────── */
  useEffect(() => {
    const fetchMp3Url = async () => {
      const mp3Key = release?.uploads?.mp3Master;
      if (!mp3Key) {
        setMp3SignedUrl(null);
        return;
      }

      // If it's already a full URL, use it directly
      if (/^https?:\/\//i.test(mp3Key)) {
        setMp3SignedUrl(mp3Key);
        return;
      }

      // Otherwise, get a signed URL from the backend
      try {
        const res = await api(`/api/assets/sign-get?key=${encodeURIComponent(mp3Key)}`);
        if (!res.ok) throw new Error('Failed to get signed URL');
        const data = await res.json();
        setMp3SignedUrl(data.url);
      } catch (err) {
        console.error('Failed to fetch MP3 signed URL:', err);
        setMp3SignedUrl(null);
      }
    };

    fetchMp3Url();
  }, [release]);

  /* ───────── handlers ───────── */
  const openEditModal = () => setIsModalOpen(true);

  const handleSaveRelease = async (draft) => {
  if (!draft.title?.trim()) {
    alert('Release title required');
    return;
  }

  try {
    // ❌ Do not let the modal save overwrite links/identifiers
    const stripKeys = [
      'presaveLink', 'soundcloudUrl', 'spotifyUrl', 'discoUrl',
      'isrc', 'trackCatalogNumber', 'trackUpc'
    ];
    const cleanDraft = { ...draft };
    stripKeys.forEach((k) => { if (k in cleanDraft) delete cleanDraft[k]; });

    const url = `/api/releases/${release._id}`;
    const res = await api(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(cleanDraft),
    });
    if (!res.ok) throw new Error(`Update failed (${res.status})`);
    const updated = await res.json();
    setRelease(updated);
    setIsModalOpen(false);
  } catch (err) {
    alert(err.message);
  }
};

  const handleDelete = async () => {
    try {
      await api(`/api/releases/${release._id}`, { method: 'DELETE' });
      navigate('/track-overview');
    } catch (err) {
      alert(err.message);
    }
  };

  const toggleArchive = async () => {
    try {
      const body = { ...release, isArchived: !release.isArchived };
      const res = await api(`/api/releases/${release._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`Archive toggle failed (${res.status})`);
      setRelease(await res.json());
    } catch (err) {
      alert(err.message);
    }
  };
  
  /* export handlers */
  const handleAppendToSheet = async () => {
    try {
      if (release.isAppendedToSheet) {
        const ok = window.confirm(
          'This release is already marked as "Added to Sheet". Do you want to append another row anyway?'
        );
        if (!ok) return;
      }

      const r = await api(`/api/export/releases/${release._id}/sheet`, { method: 'POST' });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(`Sheet append failed (${r.status}): ${t}`);
      }

      alert('Added to Google Sheet ✅');
      setRelease(prev => prev ? { ...prev, isAppendedToSheet: true } : prev);
    } catch (err) {
      alert(err.message || 'Failed to add to sheet');
    }
  };
  
  const handleAppendToDistrict = async () => {
    try {
      setDistrictAppending(true);

      const r = await api(`/api/export/releases/${release._id}/district`, { method: 'POST' });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(`District add failed (${r.status}): ${t}`);
      }

      const { processed, added, appended, skipped } = await r.json();
      const addedCount = typeof added === 'number' ? added : (typeof appended === 'number' ? appended : 0);

      alert(`Added to Automatic District ✅
Processed: ${processed}
Added: ${addedCount}
Skipped: ${skipped}`);
      
      setRelease(prev => prev ? { ...prev, isAppendedToDistrict: true } : prev);
    } catch (e) {
      alert(e.message || 'Failed to add to District');
    } finally {
      setDistrictAppending(false);
    }
  };
  
  const handleAppendToKillphonic = async () => {
    try {
      setKillphonicAppending(true);

      const r = await api(`/api/export/releases/${release._id}/killphonic`, { method: 'POST' });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(`Killphonic add failed (${r.status}): ${t}`);
      }

      const { processed, added, skipped } = await r.json();

      alert(`Added to Automatic Killphonic ✅
Processed releases: ${processed}
Rows added: ${added}
Skipped: ${skipped}`);
      
      setRelease(prev => prev ? { ...prev, isAppendedToKillphonic: true } : prev);
    } catch (e) {
      alert(e.message || 'Failed to add to Killphonic');
    } finally {
      setKillphonicAppending(false);
    }
  };

  const CREATORS_UPSERT_URL = 'https://sxvkbgwrziboygokvqmg.supabase.co/functions/v1/upsert-release';

  const handleAppendToCreators = async () => {
    const apiKey = process.env.REACT_APP_CREATORS_API_KEY;
    if (!apiKey || !apiKey.trim()) {
      alert('Creators API key is not configured. Set REACT_APP_CREATORS_API_KEY in your environment.');
      return;
    }

    const catalogue =
      release.trackCatalogNumber ||
      release.assignedProjectCatalogNumber ||
      String(release._id);

    const artist =
      Array.isArray(release.mainArtists) && release.mainArtists.length
        ? release.mainArtists.map((a) => (a && (a.name || a.title))).filter(Boolean).join(', ')
        : (Array.isArray(release.artists) && release.artists.length
            ? release.artists
                .map((a) =>
                  typeof a === 'object' && a && (a.name || a.title) ? a.name || a.title : ''
                )
                .filter(Boolean)
                .join(', ')
            : '');

    const rawDate = release.releaseDate;
    const release_date = rawDate
      ? (typeof rawDate === 'string' ? rawDate.slice(0, 10) : new Date(rawDate).toISOString().slice(0, 10))
      : '';

    const coverArt = release.coverArt || '';
    const cover_url =
      typeof coverArt === 'string' && (coverArt.startsWith('http://') || coverArt.startsWith('https://'))
        ? coverArt
        : '';

    const uploads = release.uploads || {};
    const audio_key = uploads.mp3Master || '';
    const lossless_key = uploads.losslessMaster || '';
    const cover_key = uploads.artwork || '';
    const lyrics_key = uploads.lyricsPdf || '';

    const body = {
      catalogue,
      artist: artist || 'Unknown',
      title: release.title || 'Untitled',
      isrc: release.isrc || '',
      release_date,
      fanlink: release.presaveLink || '',
      audio_url: '',
      cover_url,
      type: release.releaseType || 'Single',
      is_cover: false,
      lyrics_url: '',

      // R2 keys for direct access from the receiving app
      audio_key,
      lossless_key,
      cover_key,
      lyrics_key,
    };

    try {
      setCreatorsAppending(true);

      const response = await fetch(CREATORS_UPSERT_URL, {
        method: 'POST',
        headers: {
          'x-api-key': apiKey.trim(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.error || data?.message || `Creators API error (${response.status})`);
      }

      // Persist this status for all users
      try {
        const upd = await api(`/api/releases/${release._id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ isAppendedToCreators: true }),
        });
        if (upd.ok) {
          const updated = await upd.json();
          setRelease(updated);
        } else {
          setRelease((prev) => (prev ? { ...prev, isAppendedToCreators: true } : prev));
        }
      } catch {
        setRelease((prev) => (prev ? { ...prev, isAppendedToCreators: true } : prev));
      }

      alert('Added to Creators ✅');
    } catch (e) {
      alert(e.message || 'Failed to add to Creators');
    } finally {
      setCreatorsAppending(false);
    }
  };

const handleSaveIdentifiers = async () => {
  if (identsSaving) return; // guard against double-click
  setIdentsSaving(true);

  try {
    // Build candidate payload based on Single vs other types
    const base = {
      isrc: (identsDraft.isrc || '').trim(),
    };
    const maybeSingles = {};
    if (isSingle) {
      maybeSingles.trackCatalogNumber = (identsDraft.trackCatalogNumber || '').trim();
      maybeSingles.trackUpc           = (identsDraft.trackUpc || '').trim();
    }
    const candidate = { ...base, ...maybeSingles };

    // Diff vs existing release values, only send changes
    const diff = {};
    Object.keys(candidate).forEach((k) => {
      const current = (release[k] || '').trim();
      const next    = candidate[k];
      if (current !== next) diff[k] = next;
    });

    if (Object.keys(diff).length === 0) {
      console.log('[RD][PUT identifiers] no changes — skipping PUT');
      setIdentsEditing(false);
      return;
    }

    console.log('[RD][PUT identifiers] sending (diff only):', { id: release._id, diff });

    const res = await api(`/api/releases/${release._id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(diff),
    });
    if (!res.ok) throw new Error(`Identifiers save failed (${res.status})`);

    const updated = await res.json();
    console.log('[RD][PUT identifiers] response:', {
      id: updated?._id, isrc: updated?.isrc,
      trackCatalogNumber: updated?.trackCatalogNumber,
      trackUpc: updated?.trackUpc,
    });

    setRelease(updated);
    setIdentsEditing(false);
  } catch (err) {
    alert(err.message || 'Failed to save identifiers');
  } finally {
    setIdentsSaving(false);
  }
};

const handleSaveLinks = async () => {
  if (linksSaving) return; // guard against double-click
  setLinksSaving(true);

  try {
    // 1) Build the *trimmed draft*
    const payload = {
      presaveLink:   (linksDraft.presaveLink   || '').trim(),
      soundcloudUrl: (linksDraft.soundcloudUrl || '').trim(),
      spotifyUrl:    (linksDraft.spotifyUrl    || '').trim(),
      discoUrl:      (linksDraft.discoUrl      || '').trim(),
    };

    // 2) Diff vs current release – only send changed keys
    const changes = {};
    (['presaveLink', 'soundcloudUrl', 'spotifyUrl', 'discoUrl']).forEach((k) => {
      const current = (release[k] || '').trim();
      const next    = payload[k];
      if (current !== next) changes[k] = next;
    });

    // 3) No-op? Skip PUT entirely (prevents accidental overwrites)
    if (Object.keys(changes).length === 0) {
      console.log('[RD][PUT links] no changes — skipping PUT');
      setLinksEditing(false);
      return;
    }

    console.log('[RD][PUT links] sending (diff only):', { id: release._id, changes });

    const res = await api(`/api/releases/${release._id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(changes),
    });
    if (!res.ok) throw new Error(`Links save failed (${res.status})`);

    const updated = await res.json();
    console.log('[RD][PUT links] response:', {
      id: updated?._id,
      presaveLink:   updated?.presaveLink,
      soundcloudUrl: updated?.soundcloudUrl,
      spotifyUrl:    updated?.spotifyUrl,
      discoUrl:      updated?.discoUrl,
    });

    // 4) Use the FULL server response - it's the source of truth
    setRelease(updated);
    setLinksEditing(false);
  } catch (err) {
    alert(err.message || 'Failed to save links');
  } finally {
    setLinksSaving(false);
  }
};

  /* ───────── early guards ───────── */
  if (loading) return <div style={{ color: '#fff' }}>Loading…</div>;
  if (error) return <div style={{ color: '#fff' }}>Error: {error}</div>;
  if (!release) return <div style={{ color: '#fff' }}>Release not found</div>;

  /* helpers that need release */
  const formattedDate = release.releaseDate
    ? new Date(release.releaseDate)
        .toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: '2-digit',
        })
        .replaceAll('/', '.')
    : '';

  const artistNames = Array.isArray(release.artists)
    ? release.artists.map((a) => a.name).filter(Boolean)
    : [];

  const tplObj = templates.find((t) => String(t._id) === selectedTemplateId);
  const tplIconUrl = tplObj?.icon ? iconMap[tplObj.icon] : null;

const projectIdForLink =
  release.projectId && (typeof release.projectId === 'object'
    ? release.projectId._id
    : release.projectId);
const assetsSubmitted = !!release.isSubmitted; // true = submitted (green), false = not submitted (orange)

  /* ───────── audio player handlers ───────── */
  const handlePlayClick = () => {
    if (!mp3SignedUrl) return;
    
    // If this track is already loaded, just toggle play/pause
    if (currentTrack?.releaseId === release._id) {
      togglePlayPause();
      return;
    }
    
    playTrack({
      audioUrl: mp3SignedUrl,
      trackTitle: release.title,
      artistName: artistNames.join(', '),
      coverArt: release.coverArt || emptyArt,
      releaseId: release._id,
    });
  };

  // Check if this release is currently playing
  const isCurrentlyPlaying = currentTrack?.releaseId === release._id && globalIsPlaying;

  /* ───────── JSX ───────── */
  return (
    <div style={{ padding: 20, color: '#fff' }}>
      {/* CSS for check icon */}
      <style>{`
        .ck-check circle, .ck-check ellipse {
          fill: #28a745 !important;
          stroke: none !important;
        }
        .ck-check path, .ck-check polyline, .ck-check line {
          fill: none !important;
          stroke: #ffffff !important;
          stroke-width: 2 !important;
          stroke-linecap: round !important;
          stroke-linejoin: round !important;
        }
      `}</style>

      {/* back */}
      <button
  onClick={() => {
    if (fromCalendar) {
      // If there is a real history entry, go back. Otherwise, jump to /calendar.
      if (window.history.length > 1) navigate(-1);
      else navigate('/calendar');
    } else if (fromProject) {
      navigate(`/project/${location.state.projectId}`);
    } else {
      navigate('/track-overview');
    }
  }}
        style={{
          background: 'transparent',
          border: 'none',
          color: '#fff',
          fontSize: 24,
          cursor: 'pointer',
          marginBottom: 20,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          style={{ marginRight: 8, fill: '#333' }}
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M10 6 L8.59 7.41 13.17 12 l-4.58 4.59L10 18 l6-6z" transform="rotate(180,12,12)" />
        </svg>
      </button>

      {/* header */}
<div style={{ display: 'flex', gap: 20, marginBottom: 20, alignItems: 'flex-start' }}>        
        {/* Cover art with audio player */}
        <div 
          className="cover-art-player"
          style={{ 
            position: 'relative', 
            width: 300,
            cursor: mp3SignedUrl ? 'pointer' : 'default',
          }}
          onClick={handlePlayClick}
          title={mp3SignedUrl ? 'Play MP3 Master' : 'No MP3 Master uploaded'}
        >
          <style>{`
            .cover-art-player .play-overlay {
              opacity: ${isCurrentlyPlaying ? '0.9' : '0'};
            }
            .cover-art-player:hover .play-overlay {
              opacity: 0.7;
            }
          `}</style>
          
          <img
            src={release.coverArt || emptyArt}
            alt="cover"
            style={{ 
              width: 300, 
              borderRadius: 10,
              display: 'block',
              opacity: mp3SignedUrl ? 1 : 0.7,
            }}
          />
          
          {/* Play overlay */}
          {mp3SignedUrl && (
            <div
              className="play-overlay"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(0, 0, 0, 0.4)',
                borderRadius: 10,
                transition: 'opacity 0.2s',
                pointerEvents: 'none',
              }}
            >
              <svg
                width="80"
                height="80"
                viewBox="0 0 24 24"
                fill="white"
                style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }}
              >
                {isCurrentlyPlaying ? (
                  // Pause icon
                  <>
                    <rect x="6" y="4" width="4" height="16" />
                    <rect x="14" y="4" width="4" height="16" />
                  </>
                ) : (
                  // Play icon
                  <path d="M8 5v14l11-7z" />
                )}
              </svg>
            </div>
          )}
        </div>

        <div style={{ flexGrow: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
            {projectIdForLink ? (
  <Link
    to={`/project/${projectIdForLink}`}
    state={{ from: 'release', releaseId: release._id }}
    style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}
    title="Go to project details"
  >
    <h1 style={{ margin: 0 }}>{release.title}</h1>
  </Link>
) : (
  <h1 style={{ margin: 0 }}>{release.title}</h1>
)}
<ReleaseInfocard
  open={infoOpen}
  onClose={() => setInfoOpen(false)}
  release={release}
/>
            {/* edit + archive icons */}
            <img
  src={infoIcon}
  alt="info"
  onClick={() => setInfoOpen(true)}
  title="Release info"
  style={{
    width: 18,
    height: 18,
    cursor: 'pointer',
    filter: 'brightness(0) saturate(100%) invert(20%)',
  }}
/>
            <svg
              onClick={openEditModal}
              width="20"
              height="20"
              style={{ cursor: 'pointer', fill: '#333' }}
              viewBox="0 0 24 24"
            >
              <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM21 7.04a1.2 1.2 0 0 0 0-1.68l-2.34-2.34a1.2 1.2 0 0 0-1.68 0l-1.84 1.84 3.75 3.75L21 7.04z" />
            </svg>

            <img
              src={archiveIcon}
              alt="archive"
              onClick={() => setShowArchiveConfirm(true)}
              style={{
                width: 20,
                height: 20,
                cursor: 'pointer',
                filter: 'brightness(0) saturate(100%) invert(20%)',
              }}
            />

            {/* Assets submitted status tag */}
<span
  onClick={() => {
    if (projectIdForLink) navigate(`/asset-collection/${projectIdForLink}`);
  }}
  title={
    projectIdForLink
      ? 'Open Asset Collection'
      : 'No project linked'
  }
  style={{
    marginLeft: 6,
    padding: '4px 8px',
    borderRadius: 6,
    fontWeight: 700,
    lineHeight: 1,
    background: assetsSubmitted ? '#28a745' : '#f39c12', // green or orange
    color: '#fff',
    cursor: projectIdForLink ? 'pointer' : 'default',
    opacity: projectIdForLink ? 1 : 0.7,
    userSelect: 'none',
    display: 'inline-block',
  }}
>
  {assetsSubmitted ? 'Assets Submitted' : 'Assets not submitted'}
</span>

            {/* Email button */}
            {(() => {
              const sentEmails = Array.isArray(release?.sentEmails) ? release.sentEmails : [];
              const scheduledEmails = Array.isArray(release?.scheduledEmails) ? release.scheduledEmails : [];
              
              const releaseDetailsSent = sentEmails.includes('releaseDetails');
              const outNowSent = sentEmails.includes('outNow');
              const outNowScheduled = scheduledEmails.some(e => e.emailType === 'outNow');
              
              // Determine colors for left and right halves
              const leftColor = outNowSent ? '#4caf50' : (outNowScheduled ? '#5dade2' : '#3a3a3a'); // Green if sent, blue if scheduled, gray otherwise (Out Now Email)
              const rightColor = releaseDetailsSent ? '#4caf50' : '#3a3a3a'; // Green if sent, gray otherwise (Release Details Email)
              
              return (
                <div style={{ position: 'relative', marginLeft: 10 }}>
                  <button
                    onClick={() => setShowEmailModal(true)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      width: '36px',
                      height: '36px',
                      padding: '0',
                      backgroundColor: 'transparent',
                      borderRadius: '50%',
                      border: 'none',
                      flexShrink: 0,
                      transition: 'all 0.2s ease',
                      opacity: 0.5,
                      position: 'relative',
                      zIndex: 1,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#1f1f1f';
                      e.currentTarget.style.opacity = '1';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.opacity = '0.5';
                    }}
                    title="Send email to artist"
                  >
                    <img
                      src={mailIcon}
                      alt="Email"
                      style={{
                        width: '18px',
                        height: '18px',
                        filter: 'brightness(0) invert(90%)',
                      }}
                    />
                  </button>
                  {/* Split circle border indicator */}
                  <svg
                    width="36"
                    height="36"
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      pointerEvents: 'none',
                      zIndex: 0,
                    }}
                  >
                    {/* Left half (Out Now Email) */}
                    <path
                      d="M 18 1 A 17 17 0 0 1 18 35"
                      fill="none"
                      stroke={leftColor}
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    {/* Right half (Release Details Email) */}
                    <path
                      d="M 18 1 A 17 17 0 0 0 18 35"
                      fill="none"
                      stroke={rightColor}
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
              );
            })()}
          </div>

          {release.isArchived && (
            <span
              style={{
                display: 'inline-block',
                marginTop: 6,
                background: '#333',
                padding: '4px 8px',
                borderRadius: 5,
                color: 'gray',
              }}
            >
              Archived
            </span>
          )}

          {artistNames.length > 0 && (
            <p style={{ marginTop: 15, fontWeight: 'bold' }}>
              {artistNames.join(', ')}
            </p>
          )}

          {formattedDate && (
            <p style={{ marginTop: 5, color: '#ccc' }}>
              <strong>Release Date:</strong> {formattedDate}
            </p>
          )}

          {release.releaseType && (
            <p style={{ marginTop: 5, color: '#ccc' }}>
              <strong>Release Type:</strong> {release.releaseType}
            </p>
          )}

          {/* Export buttons row */}
          <div style={{ display: 'flex', gap: 10, marginTop: 15, flexWrap: 'wrap' }}>
            {/* Download All (.zip) */}
            <button
              onClick={() =>
                window.open(
                  `${API_BASE}/api/download/releases/${release._id}/all`,
                  '_blank',
                  'noopener,noreferrer'
                )
              }
              style={{
                padding: '8px 12px',
                background: '#444',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: '0.9rem',
              }}
              title="Download all assets as .zip"
            >
              <DownloadIcon style={{ width: 16, height: 16, fill: '#aaa' }} />
              Download All (.zip)
            </button>

            {/* Add to Sheet */}
            <button
              onClick={handleAppendToSheet}
              style={{
                padding: '8px 12px',
                background: '#444',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: '0.9rem',
              }}
              title="Append this release to the Google Sheet"
            >
              {release.isAppendedToSheet && (
                <CheckCircleIcon className="ck-check" style={{ width: 16, height: 16 }} />
              )}
              {release.isAppendedToSheet ? 'Added to Sheet' : 'Add to Sheet'}
            </button>

            {/* Add to District */}
            <button
              onClick={handleAppendToDistrict}
              disabled={districtAppending}
              style={{
                padding: '8px 12px',
                background: districtAppending ? '#555' : '#444',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: districtAppending ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: '0.9rem',
              }}
              title='Add this release to the "Automatic District" tab'
            >
              {release.isAppendedToDistrict && (
                <CheckCircleIcon className="ck-check" style={{ width: 16, height: 16 }} />
              )}
              {release.isAppendedToDistrict ? 'Added to District' : (districtAppending ? 'Adding…' : 'Add to District')}
            </button>

            {/* Add to Killphonic */}
            <button
              onClick={handleAppendToKillphonic}
              disabled={killphonicAppending || release.isAppendedToKillphonic}
              style={{
                padding: '8px 12px',
                background: killphonicAppending || release.isAppendedToKillphonic ? '#555' : '#444',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: killphonicAppending || release.isAppendedToKillphonic ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: '0.9rem',
                opacity: killphonicAppending ? 0.8 : 1,
              }}
              title='Add this release to the "Automatic Killphonic" tab'
            >
              {release.isAppendedToKillphonic && (
                <CheckCircleIcon className="ck-check" style={{ width: 16, height: 16 }} />
              )}
              {release.isAppendedToKillphonic ? 'Added to Killphonic' : (killphonicAppending ? 'Adding…' : 'Add to Killphonic')}
            </button>

            {/* Add to Creators */}
            <button
              onClick={handleAppendToCreators}
              disabled={creatorsAppending}
              style={{
                padding: '8px 12px',
                background: creatorsAppending ? '#555' : '#444',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: creatorsAppending ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: '0.9rem',
                opacity: creatorsAppending ? 0.8 : 1,
              }}
              title="Send this release to Creators"
            >
              {release.isAppendedToCreators && (
                <CheckCircleIcon className="ck-check" style={{ width: 16, height: 16 }} />
              )}
              {release.isAppendedToCreators ? 'Added to Creators' : (creatorsAppending ? 'Adding…' : 'Add to Creators')}
            </button>
          </div>

            {/* Identifiers + Links toggles */}
<div style={{ display: 'flex', gap: 8, marginTop: 15 }}>
  <button
    onClick={() => setIdentsOpen((v) => !v)}
    style={{
      padding: '6px 12px',
      background: '#444',
      color: '#fff',
      border: 'none',
      borderRadius: 6,
      cursor: 'pointer',
      fontWeight: 600,
    }}
    title="Show/Hide release identifiers"
  >
    {identsOpen ? 'Hide Identifiers' : 'Show Identifiers'}
  </button>

  <button
    onClick={() => setLinksOpen((v) => !v)}
    style={{
      padding: '6px 12px',
      background: '#444',
      color: '#fff',
      border: 'none',
      borderRadius: 6,
      cursor: 'pointer',
      fontWeight: 600,
    }}
    title="Show/Hide release links"
  >
    {linksOpen ? 'Hide Links' : 'Show Links'}
  </button>
</div>

          {overdueCount > 0 && (
            <div

            
              style={{
                marginTop: 10,
                background: '#ff4c4c',
                display: 'inline-block',
                padding: '8px 12px',
                borderRadius: 8,
                fontWeight: 'bold',
              }}
            >
              ⚠ {overdueCount} Overdue Task
              {overdueCount > 1 ? 's' : ''}
            </div>
          )}

          {/* Release Identifiers panel */}
{identsOpen && (
  <div
    style={{
      marginTop: 12,
      padding: 12,
      borderRadius: 8,
      background: '#151515',
      
    }}
  >
    <div style={{ fontWeight: 700, marginBottom: 8, color: '#ddd' }}>
      Release Identifiers
    </div>

    {/* Track identifiers only for Singles */}
    {isSingle && (
  <>
    <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 10 }}>
      <label style={{ width: 160, color: '#bbb' }}>Track Catalogue #</label>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          value={identsDraft.trackCatalogNumber}
          onChange={(e) =>
            setIdentsDraft((d) => ({ ...d, trackCatalogNumber: e.target.value }))
          }
          placeholder="e.g. CK001"
          readOnly={!identsEditing && !!release.trackCatalogNumber}
          style={{
            width: 250,
            padding: 6,
            background: '#2a2a2a',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            opacity: !identsEditing && !!release.trackCatalogNumber ? 0.7 : 1,
          }}
        />
        {/* Show button if guest is false AND (in edit OR empty) */}
        {(identsEditing || !release.trackCatalogNumber) && (
          <button
            disabled={!!assigning[`${release._id}_trackCatalog`]}
            onClick={async () => {
              setAssigning(p => ({ ...p, [`${release._id}_trackCatalog`]: true }));
              try {
                const resp = await api('/api/identifiers/assign', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ releaseId: release._id, kind: 'trackCatalog' }),
                });
                if (!resp.ok) throw new Error(await resp.text());
                const { value } = await resp.json();
                // Update draft and release immediately
                setIdentsDraft(d => ({ ...d, trackCatalogNumber: value }));
                setRelease(prev => prev ? ({ ...prev, trackCatalogNumber: value }) : prev);
                alert('Track Catalogue # assigned ✅');
              } catch (e) {
                alert(e.message || 'Failed to assign Track Catalogue #');
              } finally {
                setAssigning(p => ({ ...p, [`${release._id}_trackCatalog`]: false }));
              }
            }}
            style={{
              padding: '6px 10px',
              background: assigning[`${release._id}_trackCatalog`] ? '#555' : '#333',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: assigning[`${release._id}_trackCatalog`] ? 'not-allowed' : 'pointer',
              fontWeight: 600,
            }}
            title="Assign from Sheet"
          >
            {assigning[`${release._id}_trackCatalog`] ? 'Assigning…' : 'Assign number'}
          </button>
        )}
      </div>
    </div>
  </>
)}

    {/* ISRC */}
<div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 10 }}>
  <label style={{ width: 160, color: '#bbb' }}>ISRC</label>
  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
    <input
      value={identsDraft.isrc}
      onChange={(e) => setIdentsDraft((d) => ({ ...d, isrc: e.target.value }))}
      placeholder="e.g. DEWO71700870"
      readOnly={!identsEditing && !!release.isrc}
      style={{
        width: 250,
        padding: 6,
        background: '#2a2a2a',
        color: '#fff',
        border: 'none',
        borderRadius: 6,
        opacity: !identsEditing && !!release.isrc ? 0.7 : 1,
      }}
    />
    {(identsEditing || !release.isrc) && (
      <button
        disabled={!!assigning[`${release._id}_isrc`]}
        onClick={async () => {
          setAssigning(p => ({ ...p, [`${release._id}_isrc`]: true }));
          try {
            const resp = await api('/api/identifiers/assign', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ releaseId: release._id, kind: 'isrc' }),
            });
            if (!resp.ok) throw new Error(await resp.text());
            const { value } = await resp.json();
            setIdentsDraft(d => ({ ...d, isrc: value }));
            setRelease(prev => prev ? ({ ...prev, isrc: value }) : prev);
            alert('ISRC assigned ✅');
          } catch (e) {
            alert(e.message || 'Failed to assign ISRC');
          } finally {
            setAssigning(p => ({ ...p, [`${release._id}_isrc`]: false }));
          }
        }}
        style={{
          padding: '6px 10px',
          background: assigning[`${release._id}_isrc`] ? '#555' : '#333',
          color: '#fff',
          border: 'none',
          borderRadius: 6,
          cursor: assigning[`${release._id}_isrc`] ? 'not-allowed' : 'pointer',
          fontWeight: 600,
        }}
        title="Assign from Sheet"
      >
        {assigning[`${release._id}_isrc`] ? 'Assigning…' : 'Assign number'}
      </button>
    )}
  </div>
</div>

    {/* Track identifiers only for Singles (UPC) */}
{isSingle && (
  <>
    <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 10 }}>
      <label style={{ width: 160, color: '#bbb' }}>Track UPC</label>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          value={identsDraft.trackUpc}
          onChange={(e) =>
            setIdentsDraft((d) => ({ ...d, trackUpc: e.target.value }))
          }
          placeholder="e.g. 4260571127742"
          readOnly={!identsEditing && !!release.trackUpc}
          style={{
            width: 250,
            padding: 6,
            background: '#2a2a2a',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            opacity: !identsEditing && !!release.trackUpc ? 0.7 : 1,
          }}
        />
        {(identsEditing || !release.trackUpc) && (
          <button
            disabled={!!assigning[`${release._id}_trackUpc`]}
            onClick={async () => {
              setAssigning(p => ({ ...p, [`${release._id}_trackUpc`]: true }));
              try {
                const resp = await api('/api/identifiers/assign', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ releaseId: release._id, kind: 'trackUpc' }),
                });
                if (!resp.ok) throw new Error(await resp.text());
                const { value } = await resp.json();
                setIdentsDraft(d => ({ ...d, trackUpc: value }));
                setRelease(prev => prev ? ({ ...prev, trackUpc: value }) : prev);
                alert('Track UPC assigned ✅');
              } catch (e) {
                alert(e.message || 'Failed to assign Track UPC');
              } finally {
                setAssigning(p => ({ ...p, [`${release._id}_trackUpc`]: false }));
              }
            }}
            style={{
              padding: '6px 10px',
              background: assigning[`${release._id}_trackUpc`] ? '#555' : '#333',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: assigning[`${release._id}_trackUpc`] ? 'not-allowed' : 'pointer',
              fontWeight: 600,
            }}
            title="Assign from Sheet"
          >
            {assigning[`${release._id}_trackUpc`] ? 'Assigning…' : 'Assign number'}
          </button>
        )}
      </div>
    </div>
  </>
)}

    {/* Assigned project identifiers (read-only if present on the release) */}
    {(release.assignedProjectCatalogNumber || release.assignedProjectUpc) && (
      <>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 6 }}>
          <label style={{ width: 160, color: '#bbb' }}>Assigned Project Catalogue #</label>
          <input
            readOnly
            value={release.assignedProjectCatalogNumber || ''}
            style={{
              width: 250,
              padding: 6,
              background: '#2a2a2a',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              opacity: 0.7,
            }}
          />
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 8 }}>
          <label style={{ width: 160, color: '#bbb' }}>Assigned Project UPC</label>
          <input
            readOnly
            value={release.assignedProjectUpc || ''}
            style={{
              width: 250,
              padding: 6,
              background: '#2a2a2a',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              opacity: 0.7,
            }}
          />
        </div>
      </>
    )}

    {/* Buttons */}
    <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
      {(!release.isrc && (!isSingle || (!release.trackCatalogNumber && !release.trackUpc))) ? (
        // No identifiers yet → direct Assign
        <button
  onClick={handleSaveIdentifiers}
  disabled={identsSaving}
  style={{
    padding: '8px 12px',
    background: '#28a745',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    cursor: identsSaving ? 'not-allowed' : 'pointer',
    fontWeight: 700,
    opacity: identsSaving ? 0.7 : 1,
  }}
>
  {identsSaving ? 'Saving…' : 'Assign'}
</button>
      ) : !identsEditing ? (
        // Have identifiers → lock until Edit
        <button
          onClick={() => setIdentsEditing(true)}
          style={{
            padding: '8px 12px',
            background: '#777',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer',
            fontWeight: 600,
          }}
        >
          Edit
        </button>
      ) : (
        // Edit mode → Assign + Cancel
        <>
          <button
  onClick={handleSaveIdentifiers}
  disabled={identsSaving}
  style={{
    padding: '8px 12px',
    background: '#28a745',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    cursor: identsSaving ? 'not-allowed' : 'pointer',
    fontWeight: 700,
    opacity: identsSaving ? 0.7 : 1,
  }}
>
  {identsSaving ? 'Saving…' : 'Assign'}
</button>
          <button
            onClick={() => {
              setIdentsDraft({
                isrc: release.isrc || '',
                trackCatalogNumber: release.trackCatalogNumber || '',
                trackUpc: release.trackUpc || '',
              });
              setIdentsEditing(false);
            }}
            style={{
              padding: '8px 12px',
              background: '#555',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
        </>
      )}

      
    </div>

    
  </div>

  
)}
{/* Release Links panel */}
{linksOpen && (
  <div
    style={{
      marginTop: 12,
      padding: 12,
      borderRadius: 8,
      background: '#151515',
      // border: '1px solid #2a2a2a', // add back if you want a stroke
    }}
  >
    <div style={{ fontWeight: 700, marginBottom: 8, color: '#ddd' }}>
      Release Links
    </div>

    {/* Presave/Fanlink */}
    <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 10 }}>
      <label style={{ width: 160, color: '#bbb' }}>Presave/Fanlink</label>
      <input
        value={linksDraft.presaveLink}
        onChange={(e) => setLinksDraft((d) => ({ ...d, presaveLink: e.target.value }))}
        placeholder="https://your-presave-or-fanlink.example"
        readOnly={!linksEditing && !!release.presaveLink}
        style={{
          width: 400,
          padding: 6,
          background: '#2a2a2a',
          color: '#fff',
          border: 'none',
          borderRadius: 6,
          opacity: !linksEditing && !!release.presaveLink ? 0.7 : 1,
        }}
      />
    </div>

    {/* Soundcloud */}
    <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 10 }}>
      <label style={{ width: 160, color: '#bbb' }}>Soundcloud</label>
      <input
        value={linksDraft.soundcloudUrl}
        onChange={(e) => setLinksDraft((d) => ({ ...d, soundcloudUrl: e.target.value }))}
        placeholder="https://soundcloud.com/…"
        readOnly={!linksEditing && !!release.soundcloudUrl}
        style={{
          width: 400,
          padding: 6,
          background: '#2a2a2a',
          color: '#fff',
          border: 'none',
          borderRadius: 6,
          opacity: !linksEditing && !!release.soundcloudUrl ? 0.7 : 1,
        }}
      />
    </div>

    {/* Spotify URL */}
    <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 10 }}>
      <label style={{ width: 160, color: '#bbb' }}>Spotify URL</label>
      <input
        value={linksDraft.spotifyUrl}
        onChange={(e) => setLinksDraft((d) => ({ ...d, spotifyUrl: e.target.value }))}
        placeholder="https://open.spotify.com/track/…"
        readOnly={!linksEditing && !!release.spotifyUrl}
        style={{
          width: 400,
          padding: 6,
          background: '#2a2a2a',
          color: '#fff',
          border: 'none',
          borderRadius: 6,
          opacity: !linksEditing && !!release.spotifyUrl ? 0.7 : 1,
        }}
      />
    </div>

    {/* Disco */}
    <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 10 }}>
      <label style={{ width: 160, color: '#bbb' }}>Disco</label>
      <input
        value={linksDraft.discoUrl}
        onChange={(e) => setLinksDraft((d) => ({ ...d, discoUrl: e.target.value }))}
        placeholder="https://disco.ac/…"
        readOnly={!linksEditing && !!release.discoUrl}
        style={{
          width: 400,
          padding: 6,
          background: '#2a2a2a',
          color: '#fff',
          border: 'none',
          borderRadius: 6,
          opacity: !linksEditing && !!release.discoUrl ? 0.7 : 1,
        }}
      />
    </div>

    {/* Buttons */}
    <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
      {(!release.presaveLink &&
        !release.soundcloudUrl &&
        !release.spotifyUrl &&
        !release.discoUrl) ? (
        <button
  onClick={handleSaveLinks}
  disabled={linksSaving}
  style={{
    padding: '8px 12px',
    background: '#28a745',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    cursor: linksSaving ? 'not-allowed' : 'pointer',
    fontWeight: 700,
    opacity: linksSaving ? 0.7 : 1,
  }}
>
  {linksSaving ? 'Saving…' : 'Assign'}
</button>
      ) : !linksEditing ? (
        <button
          onClick={() => setLinksEditing(true)}
          style={{
            padding: '8px 12px',
            background: '#777',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer',
            fontWeight: 600,
          }}
        >
          Edit
        </button>
      ) : (
        <>
          <button
  onClick={handleSaveLinks}
  disabled={linksSaving}
  style={{
    padding: '8px 12px',
    background: '#28a745',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    cursor: linksSaving ? 'not-allowed' : 'pointer',
    fontWeight: 700,
    opacity: linksSaving ? 0.7 : 1,
  }}
>
  {linksSaving ? 'Saving…' : 'Assign'}
</button>
          <button
            onClick={() => {
              setLinksDraft({
                presaveLink: release.presaveLink || '',
                soundcloudUrl: release.soundcloudUrl || '',
                spotifyUrl: release.spotifyUrl || '',
                discoUrl: release.discoUrl || '',
              });
              setLinksEditing(false);
            }}
            style={{
              padding: '8px 12px',
              background: '#555',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
        </>
      )}
    </div>
  </div>
)}

        </div>
        
      </div>

      {/* tabs */}
      <div
        style={{
          borderBottom: '1px solid #333',
          display: 'flex',
          gap: 20,
        }}
      >
        <button
          onClick={() => setActiveTab('tasks')}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: activeTab === 'tasks' ? '#fff' : '#888',
            padding: '8px 0',
            fontSize: '1rem',
            borderBottom:
              activeTab === 'tasks'
                ? '3px solid #28a745'
                : '3px solid transparent',
          }}
        >
          Release Tasks
        </button>

        <button
          onClick={() => setActiveTab('spotify')}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: activeTab === 'spotify' ? '#fff' : '#888',
            padding: '8px 0',
            fontSize: '1rem',
            borderBottom:
              activeTab === 'spotify'
                ? '3px solid #28a745'
                : '3px solid transparent',
          }}
        >
          Spotify Scheduling
        </button>
      </div>

      {/* tab panel */}
      {activeTab === 'tasks' && (
        <div style={{ marginTop: 25 }}>
          {/* template selector */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ marginRight: 10 }}>
              {selectedTemplateId ? 'Change ToDo Template:' : 'Select ToDo Template:'}
            </label>
            <select
              value={selectedTemplateId}
              onChange={(e) => {
                setPendingTemplateId(e.target.value);
                setShowTemplateConfirm(true);
              }}
              style={{
                appearance: 'none',
                background: '#1e1e1e',
                color: '#fff',
                border: 'none',
                padding: '8px 40px 8px 10px',
                borderRadius: 8,
                fontFamily: 'inherit',
                backgroundImage:
                  'url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiI+PHBvbHlsaW5lIGZpbGw9IiNmZmYiIHBvaW50cz0iMCAwIDE2IDAgOCAxMCIgLz48L3N2Zz4=")',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 10px center',
                backgroundSize: '16px',
                cursor: 'pointer',
              }}
            >
              <option value="">-- None --</option>
              {templates.map((t) => (
                <option key={t._id} value={t._id}>
                  {t.title}
                </option>
              ))}
            </select>

            {selectedTemplateId && tplObj && (
              <span style={{ marginLeft: 10, display: 'inline-flex', gap: 8 }}>
                {tplIconUrl ? (
                  <img src={tplIconUrl} alt="" style={{ width: 20 }} />
                ) : (
                  <span>📄</span>
                )}
                <strong>{tplObj.title}</strong>
              </span>
            )}
          </div>

          {/* task list */}
          <SelectTodoTemplate
            releaseId={release._id}
            selectedTemplateId={selectedTemplateId}
          />
        </div>
      )}

      {activeTab === 'spotify' && (
        <div style={{ marginTop: 25 }}>
          <SpotifyScheduling
  releaseId={release._id}
  defaultDate={release.releaseDate}
  spotifyUrl={release.spotifyUrl || ''}
/>
        </div>
      )}

      {/* EDIT via shared ReleaseModal */}
      <ReleaseModal
  open={isModalOpen}
  initialRelease={{ ...release, releaseDate: release.releaseDate || '' }}
  projectContextId={release.projectId || null}
  allArtists={allArtists}
  allProjects={allProjects}
  onCancel={() => setIsModalOpen(false)}
  onSave={handleSaveRelease}
  onDelete={() => {
    setIsModalOpen(false);
    setTimeout(() => setShowDeleteConfirm(true), 0);
  }}
/>

      {/* delete confirm */}
      {showDeleteConfirm && (
        <div className="modal">
          <div
            className="modal-content"
            style={{
              width: 400,
              background: '#1e1e1e',
              padding: 20,
              borderRadius: 10,
              margin: 'auto',
              color: '#fff',
            }}
          >
            <h2>Confirm Deletion</h2>
            <p>Delete this release?</p>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button
                onClick={handleDelete}
                style={{
                  padding: '10px 20px',
                  background: '#ea5a54',
                  border: 'none',
                  color: '#fff',
                  borderRadius: 5,
                }}
              >
                Delete
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                style={{
                  padding: '10px 20px',
                  background: '#555',
                  border: 'none',
                  color: '#fff',
                  borderRadius: 5,
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* archive confirm */}
      {showArchiveConfirm && (
        <div className="modal">
          <div
            className="modal-content"
            style={{
              width: 400,
              background: '#1e1e1e',
              padding: 20,
              borderRadius: 10,
              margin: 'auto',
              color: '#fff',
            }}
          >
            <h2>Confirm</h2>
            <p>
              {release.isArchived ? 'Unarchive' : 'Archive'} this release?
            </p>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button
                onClick={() => {
                  toggleArchive();
                  setShowArchiveConfirm(false);
                }}
                style={{
                  padding: '10px 20px',
                  background: '#28a745',
                  border: 'none',
                  color: '#fff',
                  borderRadius: 5,
                }}
              >
                {release.isArchived ? 'Unarchive' : 'Archive'}
              </button>
              <button
                onClick={() => setShowArchiveConfirm(false)}
                style={{
                  padding: '10px 20px',
                  background: '#555',
                  border: 'none',
                  color: '#fff',
                  borderRadius: 5,
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Modal */}
      <ReleaseEmails
        open={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        release={release}
        onReleaseUpdate={async () => {
          // Refresh release data after email is sent
          try {
            const res = await api(`/api/releases/${id}`);
            if (res.ok) {
              const data = await res.json();
              setRelease(data);
            }
          } catch (err) {
            console.error('Failed to refresh release:', err);
          }
        }}
      />

      {/* template confirm */}
      {showTemplateConfirm && (
        <div className="modal">
          <div
            className="modal-content"
            style={{
              width: 400,
              background: '#1e1e1e',
              padding: 20,
              borderRadius: 10,
              margin: 'auto',
              color: '#fff',
            }}
          >
            <h2>Confirm Template</h2>
            <p>
              {pendingTemplateId
                ? 'Load tasks from this template?'
                : 'Remove template from release?'}
            </p>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button
                onClick={async () => {
                  try {
                    const body = {
                      ...release,
                      templateId: pendingTemplateId || null,
                    };
                    const res = await api(`/api/releases/${release._id}`, {
                      method: 'PUT',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify(body),
                    });
                    if (!res.ok) throw new Error(`Template update failed (${res.status})`);
                    setRelease(await res.json());
                    setSelectedTemplateId(pendingTemplateId);
                  } catch (err) {
                    alert(err.message);
                  } finally {
                    setPendingTemplateId('');
                    setShowTemplateConfirm(false);
                  }
                }}
                style={{
                  padding: '10px 20px',
                  background: '#28a745',
                  border: 'none',
                  color: '#fff',
                  borderRadius: 5,
                }}
              >
                Confirm
              </button>
              <button
                onClick={() => {
                  setPendingTemplateId('');
                  setShowTemplateConfirm(false);
                }}
                style={{
                  padding: '10px 20px',
                  background: '#555',
                  border: 'none',
                  color: '#fff',
                  borderRadius: 5,
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}